import { AutoCompleteProps, Form } from 'antd';
import { useDebounce } from 'hooks/useDebounce';
import { useCallback, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { apiAdresseService } from 'services/api/ApiAdresseService';
import { AddressAutoCompleteOption } from 'types/ApiAdresse';

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_DELAY = 300;

export interface AddressFieldMapping {
  address: string;
  postalCode: string;
  city: string;
}

export interface UseAddressAutocompleteOptions {
  fieldMapping?: AddressFieldMapping;
  queryKey?: string;
}

export function useAddressAutocomplete<T = any>(
  options?: UseAddressAutocompleteOptions,
) {
  const form = Form.useFormInstance<T>();
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, DEBOUNCE_DELAY);

  // Default field mapping (for backward compatibility)
  const fieldMapping: AddressFieldMapping = useMemo(
    () =>
      options?.fieldMapping || {
        address: 'adresse',
        postalCode: 'codePostal',
        city: 'ville',
      },
    [options?.fieldMapping],
  );

  const queryKey = options?.queryKey || 'addresses';

  // Only enable query when we have enough characters
  const shouldFetch = debouncedSearchText.trim().length >= MIN_SEARCH_LENGTH;

  const {
    data: addressOptions = [],
    isLoading: isSearching,
    error,
  } = useQuery<AddressAutoCompleteOption[], Error>(
    [queryKey, debouncedSearchText],
    () => apiAdresseService.searchAddresses(debouncedSearchText, 10),
    {
      enabled: shouldFetch,
      staleTime: 5 * 60 * 1000, // 5 minutes - addresses don't change frequently
      cacheTime: 10 * 60 * 1000, // 10 minutes - keep in cache
      retry: 1, // Only retry once on failure
      onError: (error) => {
        console.error('Error searching addresses:', error);
      },
    },
  );

  const handleAddressSearch: AutoCompleteProps['onSearch'] = useCallback(
    (text: string) => {
      setSearchText(text);
    },
    [],
  );

  const handleAddressSelect: AutoCompleteProps<
    string,
    AddressAutoCompleteOption
  >['onSelect'] = useCallback(
    (_value: string, option: AddressAutoCompleteOption) => {
      // Auto-fill the form fields with the selected address
      form.setFieldsValue({
        [fieldMapping.address]: option.address,
        [fieldMapping.postalCode]: option.postalCode,
        [fieldMapping.city]: option.city,
      } as any);
    },
    [form, fieldMapping],
  );

  return {
    addressOptions: shouldFetch ? addressOptions : [],
    isSearching: shouldFetch && isSearching,
    handleAddressSearch,
    handleAddressSelect,
    error,
  };
}
